import React, {memo} from 'react';
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import {alpha, SxProps, Theme} from '@mui/material/styles';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {C22_PAGE} from '@/pages/components/audit-program/c-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';

export interface C22SafeguardsViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
}

const LEVELS = ['High', 'Medium', 'Low'] as const;
type ThreatLevel = typeof LEVELS[number];

const cellBorder = {borderColor: 'divider'};
const headSx = {
    ...cellBorder,
    fontWeight: 600,
    fontSize: '0.75rem',
    py: 0.75,
    px: 0.5,
};
const bodySx = {
    ...cellBorder,
    py: 0.4,
    px: 0.5,
    verticalAlign: 'top' as const,
};

const nativeFieldSx: SxProps<Theme> = {
    width: '100%',
    minWidth: 0,
    boxSizing: 'border-box',
    m: 0,
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.23)',
    borderRadius: 1,
    px: 1,
    py: '4px',
    fontSize: (theme) => theme.typography.body2.fontSize,
    lineHeight: 1.2,
    fontFamily: 'inherit',
    color: 'text.primary',
    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
    outline: 'none',
    '&:hover': {borderColor: 'text.primary'},
    '&:focus': {
        borderColor: 'primary.main',
        boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
    },
};

function NativeInput({
    value,
    onChange,
    placeholder,
}: {
    value: string;
    onChange: (v: string) => void;
    placeholder?: string;
}) {
    return (
        <Box
            component="input"
            value={value}
            placeholder={placeholder}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
            sx={nativeFieldSx}
        />
    );
}

function TickBox({
    checked,
    onToggle,
    label,
}: {
    checked: boolean;
    onToggle: () => void;
    label: string;
}) {
    return (
        <Box
            component="input"
            type="checkbox"
            checked={checked}
            onChange={onToggle}
            aria-label={label}
            sx={{
                width: 18,
                height: 18,
                cursor: 'pointer',
                accentColor: (theme) => theme.palette.primary.main,
            }}
        />
    );
}

function resolveLevel(values: Record<string, string>, rowId: string): '' | ThreatLevel {
    const direct = values[`${rowId}.level`];
    if (direct === 'High' || direct === 'Medium' || direct === 'Low') return direct;
    // legacy comments: V / Low / Medium / High
    const legacy = (values[`${rowId}.comments`] || '').trim();
    if (legacy === 'V' || legacy === 'Low') return 'Low';
    if (legacy === 'Medium' || legacy === 'High') return legacy as ThreatLevel;
    return '';
}

const ThreatRow = memo(function ThreatRow({
    id,
    text,
    level,
    onChange,
}: {
    id: string;
    text: string;
    level: '' | ThreatLevel;
    onChange: (key: string, value: string) => void;
}) {
    return (
        <TableRow>
            <TableCell sx={bodySx}>
                <Typography variant="body2" sx={{lineHeight: 1.4}}>{text}</Typography>
            </TableCell>
            {LEVELS.map((opt) => (
                <TableCell key={opt} sx={{...bodySx, textAlign: 'center'}}>
                    <TickBox
                        checked={level === opt}
                        onToggle={() => onChange(`${id}.level`, level === opt ? '' : opt)}
                        label={`${id} ${opt}`}
                    />
                </TableCell>
            ))}
        </TableRow>
    );
});

const ResponseRow = memo(function ResponseRow({
    id,
    text,
    ticked,
    onChange,
}: {
    id: string;
    text: string;
    ticked: boolean;
    onChange: (key: string, value: string) => void;
}) {
    return (
        <TableRow>
            <TableCell sx={bodySx}>
                <Typography variant="body2" sx={{lineHeight: 1.4}}>{text}</Typography>
            </TableCell>
            <TableCell sx={{...bodySx, textAlign: 'center'}}>
                <TickBox
                    checked={ticked}
                    onToggle={() => onChange(`${id}.tick`, ticked ? '' : 'Y')}
                    label={`${id} applied`}
                />
            </TableCell>
        </TableRow>
    );
});

/** Excel C2.2 — Accounting & Tax Compliance: Safeguards Applied */
export default function C22SafeguardsView({header, values, onChange}: C22SafeguardsViewProps) {
    const partnerName = values.engagementPartnerName || values.partnerName || header.partnerName || '';

    return (
        <Box sx={{p: 2}}>
            <ProgramSectionHeader
                header={header}
                variant="a13"
                refCode="C2.2"
                values={values}
                onChange={onChange}
            />

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.4, mb: 1.5, mt: 0.5}}
            >
                {C22_PAGE.title}
            </Typography>
            <Typography variant="body2" sx={{mb: 2, lineHeight: 1.45}}>
                {C22_PAGE.intro}
            </Typography>

            {/* Nature of threat — High / Medium / Low */}
            <TableContainer sx={{overflowX: 'auto', mb: 2.5}}>
                <Table size="small" sx={{minWidth: 720, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col/>
                        <col style={{width: 72}}/>
                        <col style={{width: 72}}/>
                        <col style={{width: 72}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}>{C22_PAGE.threatSectionTitle}</TableCell>
                            {LEVELS.map((opt) => (
                                <TableCell
                                    key={opt}
                                    sx={{
                                        ...headSx,
                                        textAlign: 'center',
                                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                    }}
                                >
                                    {opt}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {C22_PAGE.threats.map((row) => (
                            <ThreatRow
                                key={row.id}
                                id={row.id}
                                text={row.text}
                                level={resolveLevel(values, row.id)}
                                onChange={onChange}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Response to threat — tick those applied */}
            <TableContainer sx={{overflowX: 'auto', mb: 1}}>
                <Table size="small" sx={{minWidth: 720, tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                    <colgroup>
                        <col/>
                        <col style={{width: 120}}/>
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headSx}>{C22_PAGE.responseSectionTitle}</TableCell>
                            <TableCell
                                sx={{
                                    ...headSx,
                                    textAlign: 'center',
                                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
                                }}
                            >
                                {C22_PAGE.responseTickLabel}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {C22_PAGE.responses.map((row) => {
                            const legacy = values[`${row.id}.comments`];
                            // r25 was previously stored as r24.tick (pre-PCL row-id align)
                            const legacyTickId = row.id === 'r25' ? values['r24.tick'] : undefined;
                            const ticked =
                                values[`${row.id}.tick`] === 'Y' ||
                                legacyTickId === 'Y' ||
                                legacy === 'V' ||
                                legacy === 'Y';
                            return (
                                <React.Fragment key={row.id}>
                                    <ResponseRow
                                        id={row.id}
                                        text={row.text}
                                        ticked={ticked}
                                        onChange={onChange}
                                    />
                                    {'hasStaffTable' in row && row.hasStaffTable ? (
                                        <TableRow>
                                            <TableCell sx={bodySx} colSpan={2}>
                                                <Table size="small" sx={{tableLayout: 'fixed', borderCollapse: 'collapse'}}>
                                                    <colgroup>
                                                        <col/>
                                                        <col style={{width: '40%'}}/>
                                                    </colgroup>
                                                    <TableHead>
                                                        <TableRow>
                                                            <TableCell sx={headSx}>{C22_PAGE.staffServiceLabel}</TableCell>
                                                            <TableCell sx={headSx}>{C22_PAGE.staffNameLabel}</TableCell>
                                                        </TableRow>
                                                    </TableHead>
                                                    <TableBody>
                                                        {C22_PAGE.staffRows.map((s) => (
                                                            <TableRow key={s.id}>
                                                                <TableCell sx={bodySx}>
                                                                    <NativeInput
                                                                        value={values[`${s.id}.service`] ?? ''}
                                                                        onChange={(v) => onChange(`${s.id}.service`, v)}
                                                                    />
                                                                </TableCell>
                                                                <TableCell sx={bodySx}>
                                                                    <NativeInput
                                                                        value={values[`${s.id}.name`] ?? ''}
                                                                        onChange={(v) => onChange(`${s.id}.name`, v)}
                                                                    />
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="body2" sx={{mt: 2, mb: 2, lineHeight: 1.45}}>
                {C22_PAGE.approvalText}
            </Typography>

            <Stack spacing={1.5} sx={{maxWidth: 640}}>
                <Box sx={{display: 'grid', gridTemplateColumns: '1fr 140px', gap: 1, alignItems: 'center'}}>
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                        <Typography variant="body2" sx={{minWidth: 140, flexShrink: 0}}>
                            {C22_PAGE.engagementPartnerLabel}
                        </Typography>
                        <NativeInput
                            value={partnerName}
                            onChange={(v) => onChange('engagementPartnerName', v)}
                        />
                    </Box>
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <NativeInput
                            value={values.engagementPartnerDate ?? ''}
                            onChange={(v) => onChange('engagementPartnerDate', v)}
                            placeholder="dd/mm/yyyy"
                        />
                    </Box>
                </Box>
                <Box sx={{display: 'grid', gridTemplateColumns: '1fr 140px', gap: 1, alignItems: 'center'}}>
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                        <Typography variant="body2" sx={{minWidth: 140, flexShrink: 0}}>
                            {C22_PAGE.secondPartnerLabel}
                        </Typography>
                        <NativeInput
                            value={values.secondPartnerName ?? ''}
                            onChange={(v) => onChange('secondPartnerName', v)}
                        />
                    </Box>
                    <Box sx={{display: 'flex', gap: 1, alignItems: 'center'}}>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <NativeInput
                            value={values.secondPartnerDate ?? ''}
                            onChange={(v) => onChange('secondPartnerDate', v)}
                            placeholder="dd/mm/yyyy"
                        />
                    </Box>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{pl: 0.5}}>
                    {C22_PAGE.secondPartnerNote}
                </Typography>
            </Stack>
        </Box>
    );
}
